import { registerRoute } from '../router';
import { apiFetch, ApiError } from '../api';
import { getUser, isTenantAdmin } from '../state';
import { escapeHtml } from '../format';
import { openModal } from '../components/modal';

interface TenantMember {
  userId: string;
  userEmail: string;
  userName: string;
  roleId: string;
  roleName: string;
  joinedAt: string;
}

interface RoleOption {
  id: string;
  name: string;
}

let members: TenantMember[] = [];
let roleOptions: RoleOption[] = [];

async function loadMembers(container: HTMLElement): Promise<void> {
  const [memberList, roles] = await Promise.all([
    apiFetch<TenantMember[]>('/api/tenant-members'),
    apiFetch<RoleOption[]>('/api/roles'),
  ]);
  members = memberList;
  roleOptions = roles;
  renderList(container);
}

function renderList(container: HTMLElement): void {
  const canManage = isTenantAdmin();
  const currentUserId = getUser()?.id;
  const listEl = container.querySelector('#members-list')!;

  listEl.innerHTML = members
    .map(
      (m) => `
    <div class="card" style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;">
      <div>
        <div style="font-weight:600;">${escapeHtml(m.userName)} ${m.userId === currentUserId ? '<span class="badge badge--muted">you</span>' : ''}</div>
        <div style="color:var(--clr-text-muted);font-size:0.85rem;margin-top:4px;">${escapeHtml(m.userEmail)}</div>
        <div style="margin-top:8px;"><span class="badge badge--orange">${escapeHtml(m.roleName)}</span></div>
      </div>
      ${
        canManage
          ? `<div style="display:flex;gap:8px;flex-shrink:0;">
               <button class="btn btn--ghost" data-action="edit" data-id="${m.userId}">Change role</button>
               <button class="btn btn--ghost" data-action="remove" data-id="${m.userId}">Remove</button>
             </div>`
          : ''
      }
    </div>`,
    )
    .join('');

  if (!canManage) return;

  listEl.querySelectorAll<HTMLButtonElement>('[data-action="edit"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const member = members.find((m) => m.userId === btn.dataset.id);
      if (member) openRoleChangeModal(container, member);
    });
  });

  listEl.querySelectorAll<HTMLButtonElement>('[data-action="remove"]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      if (!confirm('Remove this member from the tenant?')) return;
      try {
        await apiFetch(`/api/tenant-members/${btn.dataset.id}`, { method: 'DELETE' });
        await loadMembers(container);
      } catch (err) {
        alert(err instanceof ApiError ? err.message : 'Something went wrong.');
      }
    });
  });
}

function roleSelectOptions(selectedId?: string): string {
  return roleOptions.map((r) => `<option value="${r.id}" ${r.id === selectedId ? 'selected' : ''}>${escapeHtml(r.name)}</option>`).join('');
}

function openAddModal(container: HTMLElement): void {
  const { root, close } = openModal(
    'Add member',
    `
    <form id="member-form">
      <div class="field">
        <label for="member-email">Email</label>
        <input class="input" id="member-email" type="email" required placeholder="someone@example.com">
        <p style="font-size:0.8rem;color:var(--clr-text-muted);margin-top:6px;">Must already have an account — this grants an existing user access to this tenant, it doesn't create one.</p>
      </div>
      <div class="field">
        <label for="member-role">Role</label>
        <select class="input" id="member-role">${roleSelectOptions()}</select>
      </div>
      <p class="error-text" id="member-error"></p>
      <div style="display:flex;gap:10px;justify-content:flex-end;">
        <button type="button" class="btn btn--secondary" id="member-cancel">Cancel</button>
        <button type="submit" class="btn btn--primary">Add member</button>
      </div>
    </form>
  `,
  );

  root.querySelector('#member-cancel')?.addEventListener('click', close);

  root.querySelector('#member-form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const errorEl = root.querySelector('#member-error')!;
    errorEl.textContent = '';

    const payload = {
      email: (root.querySelector('#member-email') as HTMLInputElement).value.trim(),
      roleId: (root.querySelector('#member-role') as HTMLSelectElement).value,
    };

    try {
      await apiFetch('/api/tenant-members', { method: 'POST', body: JSON.stringify(payload) });
      close();
      await loadMembers(container);
    } catch (err) {
      errorEl.textContent = err instanceof ApiError ? err.message : 'Something went wrong.';
    }
  });
}

function openRoleChangeModal(container: HTMLElement, member: TenantMember): void {
  const { root, close } = openModal(
    `Change role — ${member.userName}`,
    `
    <form id="member-role-form">
      <div class="field">
        <label for="member-role-select">Role</label>
        <select class="input" id="member-role-select">${roleSelectOptions(member.roleId)}</select>
      </div>
      <p class="error-text" id="member-role-error"></p>
      <div style="display:flex;gap:10px;justify-content:flex-end;">
        <button type="button" class="btn btn--secondary" id="member-role-cancel">Cancel</button>
        <button type="submit" class="btn btn--primary">Save</button>
      </div>
    </form>
  `,
  );

  root.querySelector('#member-role-cancel')?.addEventListener('click', close);

  root.querySelector('#member-role-form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const errorEl = root.querySelector('#member-role-error')!;
    errorEl.textContent = '';

    const roleId = (root.querySelector('#member-role-select') as HTMLSelectElement).value;

    try {
      await apiFetch(`/api/tenant-members/${member.userId}`, { method: 'PATCH', body: JSON.stringify({ roleId }) });
      close();
      await loadMembers(container);
    } catch (err) {
      errorEl.textContent = err instanceof ApiError ? err.message : 'Something went wrong.';
    }
  });
}

registerRoute({
  path: '/tenant-members',
  title: 'Members',
  render: ({ container }) => {
    const canManage = isTenantAdmin();

    container.innerHTML = `
      <div class="page-header" style="display:flex;align-items:flex-start;justify-content:space-between;gap:16px;">
        <div>
          <h1>Members</h1>
          <p>Who has access to the current tenant, and with what role.</p>
        </div>
        ${canManage ? '<button class="btn btn--primary" id="add-member-btn">+ Add member</button>' : ''}
      </div>
      <div id="members-list">Loading…</div>
    `;

    container.querySelector('#add-member-btn')?.addEventListener('click', () => openAddModal(container));

    loadMembers(container).catch(() => {
      container.querySelector('#members-list')!.textContent = 'Could not load members.';
    });
  },
});
